import { DatasourceTypeEnum } from "../data-structures/enums";

// Typed settings fields per datasource type, rendered by DatasourceSettingsFields.tsx and
// serialized into the Datasource.Settings JSON column. Keys must match what the server-side
// handler reads (e.g. FortesCrmMapper.FortesCrmSettings) — they ship together in one repo so
// they stay in sync by review. Types flagged HasCustomSettings without an entry here fall
// back to a raw JSON textarea, so a server-first deploy never blocks an admin.
// Settings holds identifiers/tuning only — never secrets (those belong in AuthKey/AuthSecret).

export interface IDatasourceSettingsField {
    key: string;            // JSON property name in Datasource.Settings
    label: string;          // protocol/product terms — deliberately not localized
    required?: boolean;
    placeholder?: string;
    description?: string;
}

const registry: Record<number, IDatasourceSettingsField[]> = {
    [DatasourceTypeEnum.FortesCrmOrganization]: [
        {
            key: "tenantId",
            label: "X-Tenant-Id",
            required: true,
            placeholder: "Tenant id issued by FortesCRM",
            description: "Sent as the X-Tenant-Id header when requesting a FortesCRM API token.",
        },
    ],
};

export const getSettingsFieldsForType = (typeId: number): IDatasourceSettingsField[] | undefined => {
    return registry[typeId];
};

const parseSettings = (settingsJson: string | undefined | null): Record<string, unknown> => {
    if (!settingsJson?.trim()) return {};
    try {
        const parsed = JSON.parse(settingsJson);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

export const getDatasourceSettingValue = (settingsJson: string | undefined | null, key: string): string => {
    const value = parseSettings(settingsJson)[key];
    return value === undefined || value === null ? "" : String(value);
};

// Returns the updated JSON string, or null when the object ends up empty (keeps the DB column null).
export const setDatasourceSettingValue = (settingsJson: string | undefined | null, key: string, value: string): string | null => {
    const settings = parseSettings(settingsJson);
    if (value?.trim()) {
        settings[key] = value.trim();
    } else {
        delete settings[key];
    }
    return Object.keys(settings).length > 0 ? JSON.stringify(settings) : null;
};

export const hasMissingRequiredSettings = (typeId: number, settingsJson: string | undefined | null): boolean => {
    const fields = registry[typeId];
    if (!fields) return false; // unknown types use the raw JSON fallback; the server validates
    return fields.some(field => field.required && !getDatasourceSettingValue(settingsJson, field.key));
};

export const isValidSettingsJson = (settingsJson: string | undefined | null): boolean => {
    if (!settingsJson?.trim()) return true;
    try {
        const parsed = JSON.parse(settingsJson);
        return !!parsed && typeof parsed === "object" && !Array.isArray(parsed);
    } catch {
        return false;
    }
};
